import React from "react";
import { CustomHeading, HeadingContainer, Subtitle, TitleContainer } from "@pages/style";
import { LawsuitAppealIncident, LawsuitInfo } from "@customTypes/responses/lawsuit";
import { LawsuitMovementItem } from "../components/LawsuitMovementItem";
import { MovementTabContainer } from "../style";
import { WithPos } from "@customTypes/generics";
import useAppealIncidentsOrganizer from "../hooks/useAppealIncidentsOrganizer";
import { comparePosY, filterChildren } from "../helper";
import { AppealIncidentMovementItem } from "../components/AppealIncidentMovementItem";

type LawsuitMovementsTabProps = {
  lawsuit: LawsuitInfo;
};

const RenderMovements = ({
  appeals,
  incidents,
  rootCaseId,
}: {
  appeals: WithPos<LawsuitAppealIncident>[];
  incidents: WithPos<LawsuitAppealIncident>[];
  rootCaseId: number;
}) => {
  if (appeals.length < 1 && incidents.length < 1) return null;

  const findIncidentChildren = (incident: WithPos<LawsuitAppealIncident>, x: number) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const incidentTree: any = [
      <AppealIncidentMovementItem
        movement={incident}
        key={incident.id}
        organizedMovements={{ appeals, incidents }}
      />,
    ];

    const incidentChildren = incidents
      .sort(comparePosY)
      .filter((i) => filterChildren(i, incident.id))
      .map((incidentChild) => findIncidentChildren(incidentChild, x + 1));

    incidentTree.push(incidentChildren);
    return incidentTree;
  };

  return (
    <div>
      {incidents
        .sort(comparePosY)
        .filter((i) => filterChildren(i, rootCaseId))
        .map((incident) => findIncidentChildren(incident, 1))}
      {appeals.sort(comparePosY).map((appeal) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const appealTree: any = [
          <AppealIncidentMovementItem
            key={appeal.id}
            movement={appeal}
            organizedMovements={{ appeals, incidents }}
          />,
        ];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const appealChildren: any = incidents
          .filter((i) => filterChildren(i, appeal.id))
          .map((incident) => findIncidentChildren(incident, 1));

        appealTree.push(appealChildren);
        return appealTree;
      })}
    </div>
  );
};

const LawsuitMovementsTab: React.FC<LawsuitMovementsTabProps> = ({ lawsuit }) => {
  const organizedMovements = useAppealIncidentsOrganizer(lawsuit);

  const hasMovements =
    organizedMovements.appeals.length > 0 || organizedMovements.incidents.length > 0;

  return (
    <MovementTabContainer>
      <HeadingContainer>
        <TitleContainer>
          <CustomHeading noMargin size="sm">
            Andamentos
          </CustomHeading>
          <Subtitle color="gray" size="lg" noMargin>
            Veja todos os andamentos do seu processo em ordem cronológica.
          </Subtitle>
        </TitleContainer>
      </HeadingContainer>
      <div>
        <LawsuitMovementItem icon lawsuit={lawsuit} hasMovements={hasMovements} />

        {hasMovements && (
          <RenderMovements
            appeals={organizedMovements.appeals}
            incidents={organizedMovements.incidents}
            rootCaseId={lawsuit.id}
          />
        )}
      </div>
    </MovementTabContainer>
  );
};

export default LawsuitMovementsTab;
